import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, lastValueFrom, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

const GRAPH_SCOPES = ['https://graph.microsoft.com/.default'];

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    constructor(
        private http: HttpClient,
        private msalService: MsalService
    ) { }

    private async getGraphToken(): Promise<string> {
        const account = this.msalService.instance.getActiveAccount();

        console.log('[GraphService] getGraphToken - Acquiring Graph token for active account');

        if (!account) {
            throw new Error('[GraphService] No active MSAL account. User must be logged in.');
        }

        const tokenResponse = await lastValueFrom(
            this.msalService.acquireTokenSilent({
                scopes: GRAPH_SCOPES,
                account: account
            })
        );

        console.log('[GraphService] getGraphToken - Graph token acquired successfully');
        return tokenResponse.accessToken;
    }

    public async getUserPhoto(): Promise<any> {
        const p = new Promise<any>((resolve, reject) => {
            this.getGraphToken().then(graphToken => {
                let url = "https://graph.microsoft.com/v1.0/me/photo/$value";
                let headers: HttpHeaders = new HttpHeaders({
                    Authorization: `Bearer ${graphToken}`
                });

                console.log('[GraphService] getUserPhoto - Fetching user photo from Graph API');

                let stream$ = this.http.get(url, { headers: headers, responseType: "blob" });

                if (stream$) {
                    lastValueFrom(stream$).then((response: any) => {
                        if (response) {
                            let reader: FileReader = new FileReader();

                            reader.readAsDataURL(response);
                            reader.onloadend = () => {
                                let photo = reader.result;
                                console.log('[GraphService] getUserPhoto - Photo fetched successfully');
                                resolve(photo);
                            };
                        }
                    }).catch(err => {
                        console.error('[GraphService] getUserPhoto - Error fetching photo:', err);
                        reject(err);
                    });
                }
            }).catch(err => {
                console.error('[GraphService] getUserPhoto - Error acquiring graph token:', err);
                reject(err);
            });
        });

        return await p;
    }

    public getMemberOf(): Observable<any[]> {
        return from(this.getGraphToken()).pipe(
            switchMap(graphToken => {
                const headers = new HttpHeaders({ Authorization: `Bearer ${graphToken}` });
                return this.http.get<any>('https://graph.microsoft.com/v1.0/me/memberOf', { headers });
            }),
            map((response: any) => response?.value || [])
        );
    }

    public async getUserGroups(role: any): Promise<any> {
        let results: any = null;

        try {
            console.log('[GraphService] getUserGroups - Fetching groups from Graph API');

            const graphToken = await this.getGraphToken();

            let url = `https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=startswith(displayName, '${role}')`;
            let headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${graphToken}`,
                ConsistencyLevel: "eventual"
            });

            let stream$ = this.http.get(url, { headers: headers });
            results = await lastValueFrom(stream$);

            console.log('[GraphService] getUserGroups - Groups fetched, count:', (results as any)?.value?.length);
        } catch (error: any) {
            console.error('[GraphService] getUserGroups - Error:', error.message);
            throw error;
        }

        return results;
    }

    /**
     * Checks if the current user is a member of a specific AD group by object ID
     * Handles multiple API calls if needed (Graph API returns max 100 objects per call)
     * Stops as soon as the required group is found (no need to fetch all groups)
     * @param requiredGroupObjectId The object ID of the AD group to check membership for
     * @returns Promise<boolean> - true if user is member, false otherwise
     */
    public async isUserInRequiredGroup(requiredGroupObjectId: string): Promise<boolean> {
        try {
            console.log('[GraphService] isUserInRequiredGroup - Checking membership for group:', requiredGroupObjectId);

            const graphToken = await this.getGraphToken();
            let url = 'https://graph.microsoft.com/v1.0/me/memberOf';
            const headers = new HttpHeaders({ Authorization: `Bearer ${graphToken}` });

            // Keep fetching until we find the required group or run out of results
            // Graph API returns max 100 objects per call, use @odata.nextLink for additional results
            while (url) {
                console.log(`[GraphService] isUserInRequiredGroup - Fetching groups from: ${url}`);
                
                const response = await lastValueFrom(this.http.get<any>(url, { headers }));
                const groups = response?.value || [];

                console.log(`[GraphService] isUserInRequiredGroup - Received ${groups.length} groups in this response`);

                // Check if the required group is in the current response
                const isMember = groups.some((group: any) => group.id === requiredGroupObjectId);

                if (isMember) {
                    console.log('[GraphService] isUserInRequiredGroup - User is member of required group:', requiredGroupObjectId);
                    return true;
                }

                // Check if there are more results to fetch
                url = response?.['@odata.nextLink'];

                if (url) {
                    console.log('[GraphService] isUserInRequiredGroup - More results available, fetching next batch via @odata.nextLink');
                } else {
                    console.log('[GraphService] isUserInRequiredGroup - No more results available');
                }
            }

            console.log('[GraphService] isUserInRequiredGroup - User is NOT member of required group:', requiredGroupObjectId);
            return false;

        } catch (error: any) {
            console.error('[GraphService] isUserInRequiredGroup - Error checking group membership:', error.message);
            throw error;
        }
    }

    /**
     * Check which roles (groups) the user is a member of
     * Fetches user's groups once and compares against roleGroupMapping
     * @param roleGroupMapping Object mapping role names to group IDs
     * @returns Promise<string[]> - Array of roles user is member of
     */
    public async getUserRoles(roleGroupMapping: { [key: string]: string }): Promise<string[]> {
        try {
            console.log('[GraphService] getUserRoles - Checking user roles against mapping:', Object.keys(roleGroupMapping));

            const graphToken = await this.getGraphToken();
            const headers = new HttpHeaders({ Authorization: `Bearer ${graphToken}` });
            
            const userRoles: string[] = [];
            let url = 'https://graph.microsoft.com/v1.0/me/memberOf';
            const allUserGroupIds = new Set<string>();

            // Fetch all user's groups
            while (url) {
                console.log(`[GraphService] getUserRoles - Fetching groups batch from: ${url}`);
                
                const response = await lastValueFrom(this.http.get<any>(url, { headers }));
                const groups = response?.value || [];

                // Store all group IDs user is member of
                groups.forEach((group: any) => {
                    allUserGroupIds.add(group.id);
                });

                console.log(`[GraphService] getUserRoles - Received ${groups.length} groups, total collected: ${allUserGroupIds.size}`);

                url = response?.['@odata.nextLink'];
            }

            // Check which roles user is member of
            for (const [roleName, groupId] of Object.entries(roleGroupMapping)) {
                if (allUserGroupIds.has(groupId)) {
                    userRoles.push(roleName);
                    console.log(`[GraphService] getUserRoles - User is member of role: ${roleName}`);
                } else {
                    console.log(`[GraphService] getUserRoles - User is NOT member of role: ${roleName}`);
                }
            }

            console.log('[GraphService] getUserRoles - User roles:', userRoles);
            return userRoles;

        } catch (error: any) {
            console.error('[GraphService] getUserRoles - Error fetching user roles:', error.message);
            throw error;
        }
    }
}
